import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { TableViewProps } from '@/type/listing.types';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { usePageSize } from '@/hooks/page-size.hooks';
import { SETTINGS_TIPS } from '@/docs/tips/settings';
import { RichSelect } from '@/components/Controls/RichSelect';
import {
  AdvancedFilterPanel,
  AdvancedFilterToggle,
  FilterField,
  FilterGrid,
  getStatusPillOptions,
} from '@/components/Custom/Filter/FilterKit';
import { IconBriefcase, IconUser, IconUsers } from '@tabler/icons-react';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useVehicleCheckTypeFilter } from '@/hooks/filters/vehicle-check-type-filter.hooks';

export const VehiculeCheckTypeTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { pageSize: limit, setPageSize } = usePageSize();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    type,
    status,
    debounceFilterUpdate,
    searchByStatus,
    searchByType,
  } = useVehicleCheckTypeFilter({ setCurrentPage });
  const advancedFiltersCount = [type].filter(Boolean).length;
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle
              textClassName="mt-1.5"
              text={t('common:inspectionTypes')}
              infoTip={SETTINGS_TIPS.inspectionTypesTable}
            />
            <div className="hidden max-md:block">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
            <div className="flex items-center flex-1 gap-3.5 min-w-[300px] max-1.5xl:min-w-0 max-1.5xl:basis-full">
              <Input
                placeholder={t('searchInspectionType')}
                type="text"
                name="search"
                autoComplete="off"
                leftIcon={
                  <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
                }
                inputWrapperClassName="w-full"
                inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                value={key}
                onChange={(e) => debounceFilterUpdate(e.target.value)}
              />
              <div className="hidden max-1.5xl:block max-md:hidden">
                <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
              </div>
            </div>
            <RichSelect
              containerClassName="w-auto min-w-[180px] max-1.5xl:min-w-0 max-sm:basis-full"
              placeholder={t('common:allStates')}
              aria-label={t('common:status')}
              options={getStatusPillOptions(
                t('common:active'),
                t('common:inactive'),
              )}
              value={status}
              onValueChange={searchByStatus}
            />
            <div className="flex justify-end max-sm:w-full">
              <AdvancedFilterToggle
                open={showAdvancedFilters}
                activeCount={advancedFiltersCount}
                onToggle={() => setShowAdvancedFilters((prev) => !prev)}
              />
            </div>
            <div className="max-1.5xl:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
        </div>
        <AdvancedFilterPanel open={showAdvancedFilters}>
          <FilterGrid>
            <FilterField
              icon={IconUsers}
              tone="indigo"
              label={t('common:performedBy')}
            >
              <RichSelect
                placeholder={t('common:proAndIndividual')}
                aria-label={t('common:performedBy')}
                options={[
                  {
                    label: t('common:professional'),
                    value: ActionUserType.PROFESSIONAL,
                    icon: (
                      <IconBriefcase size={16} className="text-indigo-600" />
                    ),
                  },
                  {
                    label: t('common:individual'),
                    value: ActionUserType.INDIVIDUAL,
                    icon: <IconUser size={16} className="text-emerald-600" />,
                  },
                ]}
                value={type}
                onValueChange={searchByType}
              />
            </FilterField>
          </FilterGrid>
        </AdvancedFilterPanel>
        <div>
          <Datatable
            t={t}
            headers={<TableHeaders t={t} onSort={handleSort} />}
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            totalItems={isLoadingAllData ? undefined : allData?.data?.['total']}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
